import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, HeartPulse } from "lucide-react";
import { useProfile } from "@/context/ProfileContext";
import { usePulseCheckInNudge } from "@/hooks/use-pulse-check-in-nudge";
import { formatPulsePeriodLabel } from "@/lib/pulse-period-label";
import { cn } from "@/lib/utils";
import { PULSE_OVERDUE_DAYS, daysSincePeriodEnd } from "@/lib/pulse-banner";

const PULSE_PATH = "/coach/performance/pulse";

function snoozeKey(businessId: string, periodStart: string): string {
  return `pulse-banner-snooze:${businessId}:${periodStart}`;
}

/** Tomorrow 07:00 local: "remind me tomorrow" means the next working morning. */
function tomorrowMorningMs(now: Date = new Date()): number {
  const d = new Date(now);
  d.setDate(d.getDate() + 1);
  d.setHours(7, 0, 0, 0);
  return d.getTime();
}

function readSnoozedUntil(key: string): number | null {
  try {
    const raw = window.localStorage.getItem(key);
    const ms = raw == null ? NaN : Number(raw);
    return Number.isFinite(ms) ? ms : null;
  } catch {
    return null;
  }
}

/**
 * "Dags för Pulse check-in" on every page, not only /coach (GRA-60). Shown to
 * whoever can submit Pulse once their own check-in period has ended and not
 * everyone is rated — the same rule as the Today nudge. It can be put off
 * until tomorrow morning but not closed for the period: the check-in is the
 * team leader's job. It turns red once the check-in is
 * `PULSE_OVERDUE_DAYS` days late, and disappears by itself when done.
 */
export function PulseCheckInBanner() {
  const { t } = useTranslation("common");
  const { businessProfile, memberRole } = useProfile();
  const { pathname } = useLocation();
  const nudge = usePulseCheckInNudge(businessProfile, memberRole);
  const businessId = businessProfile?.uid ?? "";
  const summary = nudge.summary;
  const periodStart = summary?.checkInPeriod.periodStart ?? "";
  const key = snoozeKey(businessId, periodStart);

  const [snoozedUntil, setSnoozedUntil] = useState<number | null>(null);
  useEffect(() => {
    setSnoozedUntil(periodStart ? readSnoozedUntil(key) : null);
  }, [key, periodStart]);

  if (!nudge.show || summary == null) return null;
  // Redundant on the page where the check-in is done.
  if (pathname.startsWith(PULSE_PATH)) return null;
  if (snoozedUntil != null && Date.now() < snoozedUntil) return null;

  const { periodEnd } = summary.checkInPeriod;
  const overdue = daysSincePeriodEnd(periodEnd) >= PULSE_OVERDUE_DAYS;
  const label = formatPulsePeriodLabel(periodStart, periodEnd);
  const params = new URLSearchParams({ periodStart, periodEnd });

  const snooze = () => {
    const until = tomorrowMorningMs();
    try {
      window.localStorage.setItem(key, String(until));
    } catch {
      // Private mode or blocked storage: hide it for this session only.
    }
    setSnoozedUntil(until);
  };

  const Icon = overdue ? AlertTriangle : HeartPulse;
  return (
    // Same insets and corners as the page card below it (AppLayout's content
    // area: `pr-2`, `sm:py-4`, `rounded-xl border`), so it reads as part of
    // the layout rather than a strip across it.
    <div className="pr-2 pt-2 sm:pt-4">
      <div
        role="status"
        className={cn(
          "rounded-xl border",
          overdue
            ? "border-red-600 bg-red-600 text-white"
            : "border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-100",
        )}
      >
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-2.5">
          <Icon className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1 text-sm leading-5">
            <span className="font-semibold">
              {overdue ? t("pulseBanner.titleOverdue") : t("pulseBanner.title")}
            </span>{" "}
            {summary.scoredCount > 0
              ? t("pulseBanner.progress", {
                  done: summary.scoredCount,
                  total: summary.totalMembers,
                  period: label,
                })
              : t("pulseBanner.notStarted", { period: label })}
          </span>
          <div className="flex shrink-0 items-center gap-3">
            <button
              type="button"
              onClick={snooze}
              className={cn(
                "text-xs underline underline-offset-2",
                overdue
                  ? "text-white/85 hover:text-white"
                  : "text-amber-900/80 hover:text-amber-950 dark:text-amber-100/80",
              )}
            >
              {t("pulseBanner.later")}
            </button>
            <Link
              to={`${PULSE_PATH}?${params.toString()}`}
              className={cn(
                "rounded-md px-3 py-1 text-sm font-medium",
                overdue
                  ? "bg-white text-red-700 hover:bg-white/90"
                  : "bg-amber-600 text-white hover:bg-amber-700",
              )}
            >
              {t("pulseBanner.start")}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
